import React, { useEffect, useState, useRef, KeyboardEvent } from 'react';
import {
  Box,
  Typography,
  Button,
  IconButton,
  Tooltip,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  Paper,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  MenuItem,
  CircularProgress,
  Alert,
  Divider,
  List,
  ListItem,
  ListItemText,
  Breadcrumbs,
  InputAdornment,
  FormControlLabel,
  Switch,
  FormControl,
  InputLabel,
  FormHelperText,
  Checkbox,
  LinearProgress,
} from '@mui/material';
import { FormNavigationProvider, NavTextField, NavSelect } from '../components/FormNavigation';
import RefreshIcon from '@mui/icons-material/Refresh';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import LockIcon from '@mui/icons-material/Lock';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import FileUploadIcon from '@mui/icons-material/FileUpload';
import DashboardLayout from '../components/DashboardLayout';
import { useAppDispatch, useAppSelector } from '../store';
import { fetchCustomFields, addCustomField, removeCustomField, updateCustomField } from '../store/customFieldsSlice';
import { fetchUIFields } from '../store/applicationSettingsSlice';
import { showNotification } from '../store/notificationSlice';
import { apiService } from '../services/api';
import type { CustomField, CreateCustomFieldPayload, PatchCustomFieldPayload, SetHistoryPayload, FieldType, FontType } from '../types/customFields';

const FIELD_TYPES = ['string', 'long', 'double', 'enum', 'boolean', 'date', 'date_time'];
const NUMERIC_TYPES = ['long', 'double'];

function fieldTypeLabel(ft: string): string {
  const map: Record<string, string> = {
    string: 'String', long: 'Long', double: 'Double', enum: 'Enum',
    boolean: 'Boolean', date: 'Date', date_time: 'Date Time',
  };
  return map[ft] ?? ft;
}

function fontTypeLabel(ft: string): string {
  return ft === 'uni_code' ? 'English' : ft === 'bijoy' ? 'বাংলা' : ft;
}

function titleToFieldName(title: string): string {
  const words = title.trim().split(/\s+/)
    .map(w => w.replace(/[^a-zA-Z0-9_$]/g, ''))
    .filter(Boolean);
  while (words.length > 0 && /^\d/.test(words[0])) words.shift();
  return words.map((w, i) => {
    const word = w.toLowerCase();
    if (i === 0) return word;
    return word.charAt(0).toUpperCase() + word.slice(1);
  }).join('');
}

// ─── Enum values editor ────────────────────────────────────────────────────────

interface EnumEditorProps {
  values: string[];
  onChange: (values: string[]) => void;
  lockedValues?: string[]; // values that cannot be removed (locked field)
  fieldError?: string;
}

function EnumValuesEditor({ values, onChange, lockedValues = [], fieldError }: EnumEditorProps) {
  const [input, setInput] = useState('');

  function add() {
    const val = input.trim();
    if (!val) return;
    if (values.map((v) => v.toLowerCase()).includes(val.toLowerCase())) return;
    onChange([...values, val]);
    setInput('');
  }

  function remove(idx: number) {
    const val = values[idx];
    if (lockedValues.map((v) => v.toLowerCase()).includes(val.toLowerCase())) return;
    onChange(values.filter((_, i) => i !== idx));
  }

  function onKeyDown(e: KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'Enter') { e.preventDefault(); add(); }
  }

  return (
    <Box>
      <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, textTransform: 'uppercase', fontSize: '0.7rem' }}>
        Possible Values
      </Typography>
      <Box sx={{ display: 'flex', gap: 1, mt: 0.5, mb: 0.5 }}>
        <NavTextField
          placeholder="Add a value"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={onKeyDown}
          size="small"
          sx={{ flex: 1 }}
          error={!!fieldError && values.length < 2}
          navSkip
        />
        <Button variant="outlined" size="small" onClick={add} startIcon={<AddIcon />}>
          Add
        </Button>
      </Box>
      {fieldError && (
        <Typography variant="caption" color="error" sx={{ display: 'block', mb: 0.5 }}>
          {fieldError}
        </Typography>
      )}
      <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, height: 200, overflowY: 'auto' }}>
        {values.length === 0 ? (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
            <Typography variant="caption" color="text.secondary">No values added yet. At least 2 required.</Typography>
          </Box>
        ) : (
          <List dense disablePadding>
            {values.map((v, idx) => {
              const isLocked = lockedValues.map((lv) => lv.toLowerCase()).includes(v.toLowerCase());
              return (
                <ListItem
                  key={idx}
                  disablePadding
                  sx={{ px: 1.5, py: 0.25, borderBottom: idx < values.length - 1 ? '1px solid' : 'none', borderColor: 'divider' }}
                  secondaryAction={
                    <Tooltip title={isLocked ? 'Cannot remove (field is locked)' : 'Remove'}>
                      <span>
                        <IconButton
                          size="small"
                          color="error"
                          edge="end"
                          onClick={() => remove(idx)}
                          disabled={isLocked}
                        >
                          <RemoveCircleOutlineIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                  }
                >
                  <ListItemText
                    primary={
                      <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                        {v}
                        {isLocked && (
                          <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>(locked)</Typography>
                        )}
                      </Typography>
                    }
                  />
                </ListItem>
              );
            })}
          </List>
        )}
      </Box>
    </Box>
  );
}

// ─── Shared font type select ───────────────────────────────────────────────────

function FontTypeSelect({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  return (
    <FormControl fullWidth size="small" sx={{ mb: 2 }}>
      <InputLabel>Font Type</InputLabel>
      <NavSelect
        value={value}
        label="Font Type"
        onChange={(e) => onChange(e.target.value as string)}
      >
        <MenuItem value="uni_code">English</MenuItem>
        <MenuItem value="bijoy">বাংলা</MenuItem>
      </NavSelect>
    </FormControl>
  );
}

// ─── Create dialog ─────────────────────────────────────────────────────────────

interface CreateDialogProps {
  open: boolean;
  onClose: () => void;
  onCreated: (field: CustomField) => void;
}

function CreateCustomFieldDialog({ open, onClose, onCreated }: CreateDialogProps) {
  const dispatch = useAppDispatch();
  const [autoGenerate, setAutoGenerate] = useState(true);
  const [fieldName, setFieldName] = useState('');
  const [fieldType, setFieldType] = useState('string');
  const [fieldTitle, setFieldTitle] = useState('');
  const [fontType, setFontType] = useState('uni_code');
  const [enumValues, setEnumValues] = useState<string[]>([]);
  const [maxValue, setMaxValue] = useState('');
  const [minValue, setMinValue] = useState('');
  const [historyEnabled, setHistoryEnabled] = useState(false);
  const [allowDualValues, setAllowDualValues] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const navContainerRef = React.useRef<HTMLElement | null>(null);

  useEffect(() => {
    if (!open) return;
    setAutoGenerate(true); setFieldName(''); setFieldType('string'); setFieldTitle('');
    setFontType('uni_code'); setEnumValues([]); setMaxValue(''); setMinValue('');
    setHistoryEnabled(false); setAllowDualValues(false); setError(null); setFieldErrors({}); setSubmitting(false);
  }, [open]);

  function handleTitleChange(value: string) {
    setFieldTitle(value);
    setFieldErrors((p) => ({ ...p, fieldTitle: '' }));
    if (autoGenerate) {
      setFieldName(titleToFieldName(value));
      setFieldErrors((p) => ({ ...p, fieldName: '' }));
    }
  }

  async function handleSubmit() {
    setError(null); setFieldErrors({}); setSubmitting(true);
    try {
      const payload: CreateCustomFieldPayload = {
        fieldName: fieldName.trim(),
        fieldType,
        fieldTitle: fieldTitle.trim(),
        historyEnabled,
        allowDualValues,
      };
      if (fieldType === 'string') payload.fontType = fontType;
      if (fieldType === 'enum') payload.possibleValues = enumValues;
      if (NUMERIC_TYPES.includes(fieldType)) {
        payload.maxValue = maxValue !== '' ? Number(maxValue) : null;
        payload.minValue = minValue !== '' ? Number(minValue) : null;
      }
      const response = await apiService.createCustomField(payload);
      onCreated(response.data);
      onClose();
    } catch (err: any) {
      if (err?.jsonMessage) {
        setFieldErrors(err.jsonMessage);
        if (err.jsonMessage.fieldName && autoGenerate) {
          setAutoGenerate(false);
          dispatch(showNotification({ message: 'Auto-generated field name is not valid. Please enter a field name manually.', severity: 'warning' }));
        }
      } else {
        setError(err?.message ?? 'Failed to create custom field');
      }
    } finally {
      setSubmitting(false);
    }
  }

  const isEnum = fieldType === 'enum';
  const isNumeric = NUMERIC_TYPES.includes(fieldType);
  const isString = fieldType === 'string';
  const isDualValuesSupported = fieldType === 'string' || fieldType === 'long' || fieldType === 'double';

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ ref: navContainerRef }}
    >
      <FormNavigationProvider containerRef={navContainerRef}>
        <DialogTitle>Create Custom Field</DialogTitle>
        <DialogContent dividers>
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
          <NavTextField
            label="Field Title"
            value={fieldTitle}
            onChange={(e) => handleTitleChange(e.target.value)}
            fullWidth
            required
            size="small"
            sx={{ mb: fieldErrors.fieldTitle ? 0.5 : 2 }}
            error={!!fieldErrors.fieldTitle}
            helperText={fieldErrors.fieldTitle}
          />
          <NavTextField
            label="Field Name"
            value={fieldName}
            onChange={(e) => { setFieldName(e.target.value); setFieldErrors((p) => ({ ...p, fieldName: '' })); }}
            fullWidth
            required
            size="small"
            disabled={autoGenerate}
            sx={{ mb: fieldErrors.fieldName ? 0.5 : 2 }}
            error={!!fieldErrors.fieldName}
            helperText={fieldErrors.fieldName || (autoGenerate ? 'Auto-generated from title' : 'Enter field name manually')}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <Tooltip title={autoGenerate ? 'Switch to manual input' : 'Switch to auto-generate'}>
                    <IconButton size="small" onClick={() => setAutoGenerate((v) => !v)} edge="end">
                      {autoGenerate ? <EditIcon fontSize="small" /> : <AutorenewIcon fontSize="small" />}
                    </IconButton>
                  </Tooltip>
                </InputAdornment>
              ),
            }}
          />
          <FormControl fullWidth size="small" sx={{ mb: fieldErrors.fieldType ? 0.5 : 2 }} error={!!fieldErrors.fieldType}>
            <InputLabel>Field Type</InputLabel>
            <NavSelect
              label="Field Type"
              value={fieldType}
              onChange={(e) => { setFieldType(e.target.value as string); setFieldErrors((p) => ({ ...p, fieldType: '' })); }}
            >
              {FIELD_TYPES.map((ft) => (
                <MenuItem key={ft} value={ft}>{fieldTypeLabel(ft)}</MenuItem>
              ))}
            </NavSelect>
            {fieldErrors.fieldType && <FormHelperText>{fieldErrors.fieldType}</FormHelperText>}
          </FormControl>
          {isString && <FontTypeSelect value={fontType} onChange={setFontType} />}
          {isEnum && (
            <Box sx={{ mb: 2 }}>
              <EnumValuesEditor
                values={enumValues}
                onChange={setEnumValues}
                fieldError={fieldErrors.possibleValues}
              />
            </Box>
          )}
          {isNumeric && (
            <Box sx={{ display: 'flex', gap: 2 }}>
              <NavTextField
                label="Min Value"
                value={minValue}
                onChange={(e) => { setMinValue(e.target.value); setFieldErrors((p) => ({ ...p, minValue: '' })); }}
                size="small"
                sx={{ flex: 1 }}
                error={!!fieldErrors.minValue}
                helperText={fieldErrors.minValue || 'Leave blank for default'}
                navFieldType="number"
              />
              <NavTextField
                label="Max Value"
                value={maxValue}
                onChange={(e) => { setMaxValue(e.target.value); setFieldErrors((p) => ({ ...p, maxValue: '' })); }}
                size="small"
                sx={{ flex: 1 }}
                error={!!fieldErrors.maxValue}
                helperText={fieldErrors.maxValue || 'Leave blank for default'}
                navFieldType="number"
              />
            </Box>
          )}
          <FormControlLabel
            sx={{ mt: 1 }}
            control={<Switch checked={historyEnabled} onChange={(e) => setHistoryEnabled(e.target.checked)} size="small" />}
            label="Enable history tracking"
          />
          {isDualValuesSupported && (
            <FormControlLabel
              sx={{ mt: 1 }}
              control={<Switch checked={allowDualValues} onChange={(e) => setAllowDualValues(e.target.checked)} size="small" />}
              label="Allow dual values"
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} disabled={submitting}>Cancel</Button>
          <Button variant="contained" onClick={handleSubmit} disabled={submitting}>
            {submitting ? <CircularProgress size={18} /> : 'Create'}
          </Button>
        </DialogActions>
      </FormNavigationProvider>
    </Dialog>
  );
}

// ─── Edit (Patch) dialog ───────────────────────────────────────────────────────

interface EditDialogProps {
  field: CustomField | null;
  onClose: () => void;
  onUpdated: (oldName: string, field: CustomField) => void;
}

function EditCustomFieldDialog({ field, onClose, onUpdated }: EditDialogProps) {
  const [fieldName, setFieldName] = useState('');
  const [fieldType, setFieldType] = useState('string');
  const [fieldTitle, setFieldTitle] = useState('');
  const [fontType, setFontType] = useState('uni_code');
  const [enumValues, setEnumValues] = useState<string[]>([]);
  const [maxValue, setMaxValue] = useState('');
  const [minValue, setMinValue] = useState('');
  const [allowDualValues, setAllowDualValues] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const navContainerRef = React.useRef<HTMLElement | null>(null);

  useEffect(() => {
    if (!field) return;
    setFieldName(field.fieldName);
    setFieldType(field.fieldType);
    setFieldTitle(field.fieldTitle);
    setFontType(field.fontType ?? 'uni_code');
    setEnumValues(field.possibleValues ?? []);
    setMaxValue(field.maxValue != null ? String(field.maxValue) : '');
    setMinValue(field.minValue != null ? String(field.minValue) : '');
    setAllowDualValues(field.allowDualValues ?? false);
    setError(null); setFieldErrors({}); setSubmitting(false);
  }, [field]);

  async function handleSubmit() {
    if (!field) return;
    setError(null); setFieldErrors({}); setSubmitting(true);
    try {
      const payload: PatchCustomFieldPayload = { updated_at: field.updated_at };
      if (fieldType !== field.fieldType) payload.fieldType = fieldType;
      if (fieldTitle.trim() !== field.fieldTitle) payload.fieldTitle = fieldTitle.trim();
      if (fieldType === 'string' && fontType !== field.fontType) payload.fontType = fontType;
      if (fieldType === 'enum') payload.possibleValues = enumValues;
      if (allowDualValues !== field.allowDualValues) payload.allowDualValues = allowDualValues;
      if (NUMERIC_TYPES.includes(fieldType)) {
        payload.maxValue = maxValue !== '' ? Number(maxValue) : null;
        payload.minValue = minValue !== '' ? Number(minValue) : null;
      }
      const response = await apiService.patchCustomField(field.fieldName, payload);
      onUpdated(field.fieldName, response.data);
      onClose();
    } catch (err: any) {
      if (err?.jsonMessage) setFieldErrors(err.jsonMessage);
      else setError(err?.message ?? 'Failed to update custom field');
    } finally {
      setSubmitting(false);
    }
  }

  const isEnum = fieldType === 'enum';
  const isNumeric = NUMERIC_TYPES.includes(fieldType);
  const isString = fieldType === 'string';
  const isLocked = field?.locked ?? false;
  const lockedEnumValues = isLocked ? (field?.possibleValues ?? []) : [];
  const isDualValuesSupported = fieldType === 'string' || fieldType === 'long' || fieldType === 'double';

  return (
    <Dialog
      open={!!field}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ ref: navContainerRef }}
    >
      <FormNavigationProvider containerRef={navContainerRef}>
        <DialogTitle>Edit Custom Field</DialogTitle>
        <DialogContent dividers>
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
          {isLocked && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              This field is locked. Rename and type changes are not allowed.
            </Alert>
          )}
          <NavTextField
            label="Field Name"
            value={fieldName}
            fullWidth
            size="small"
            sx={{ mb: 2 }}
            disabled
            helperText="Field name cannot be changed after creation"
          />
          <NavTextField
            label="Field Title"
            value={fieldTitle}
            onChange={(e) => { setFieldTitle(e.target.value); setFieldErrors((p) => ({ ...p, fieldTitle: '' })); }}
            fullWidth
            size="small"
            sx={{ mb: fieldErrors.fieldTitle ? 0.5 : 2 }}
            error={!!fieldErrors.fieldTitle}
            helperText={fieldErrors.fieldTitle}
          />
          <FormControl fullWidth size="small" sx={{ mb: fieldErrors.fieldType ? 0.5 : 2 }} error={!!fieldErrors.fieldType}>
            <InputLabel>Field Type</InputLabel>
            <NavSelect
              label="Field Type"
              value={fieldType}
              disabled={isLocked}
              onChange={(e) => { setFieldType(e.target.value as string); setFieldErrors((p) => ({ ...p, fieldType: '' })); }}
            >
              {FIELD_TYPES.map((ft) => (
                <MenuItem key={ft} value={ft}>{fieldTypeLabel(ft)}</MenuItem>
              ))}
            </NavSelect>
            {fieldErrors.fieldType && <FormHelperText>{fieldErrors.fieldType}</FormHelperText>}
          </FormControl>
          {isString && <FontTypeSelect value={fontType} onChange={setFontType} />}
          {isEnum && (
            <Box sx={{ mb: 2 }}>
              <EnumValuesEditor
                values={enumValues}
                onChange={setEnumValues}
                lockedValues={lockedEnumValues}
                fieldError={fieldErrors.possibleValues}
              />
            </Box>
          )}
          {isNumeric && (
            <Box sx={{ display: 'flex', gap: 2 }}>
              <NavTextField
                label="Min Value"
                value={minValue}
                onChange={(e) => { setMinValue(e.target.value); setFieldErrors((p) => ({ ...p, minValue: '' })); }}
                size="small"
                sx={{ flex: 1 }}
                error={!!fieldErrors.minValue}
                helperText={fieldErrors.minValue}
                navFieldType="number"
              />
              <NavTextField
                label="Max Value"
                value={maxValue}
                onChange={(e) => { setMaxValue(e.target.value); setFieldErrors((p) => ({ ...p, maxValue: '' })); }}
                size="small"
                sx={{ flex: 1 }}
                error={!!fieldErrors.maxValue}
                helperText={fieldErrors.maxValue}
                navFieldType="number"
              />
            </Box>
          )}
          {isDualValuesSupported && (
            <FormControlLabel
              sx={{ mt: 2 }}
              control={
                <Switch
                  checked={allowDualValues}
                  onChange={(e) => { setAllowDualValues(e.target.checked); setFieldErrors((p) => ({ ...p, allowDualValues: '' })); }}
                  disabled={isLocked && field?.allowDualValues}
                  size="small"
                />
              }
              label="Allow dual values"
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} disabled={submitting}>Cancel</Button>
          <Button variant="contained" onClick={handleSubmit} disabled={submitting}>
            {submitting ? <CircularProgress size={18} /> : 'Save'}
          </Button>
        </DialogActions>
      </FormNavigationProvider>
    </Dialog>
  );
}

// ─── Delete dialog ─────────────────────────────────────────────────────────────

interface DeleteDialogProps {
  field: CustomField | null;
  onClose: () => void;
  onDeleted: (fieldName: string) => void;
}

function DeleteCustomFieldDialog({ field, onClose, onDeleted }: DeleteDialogProps) {
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => { if (!field) { setError(null); setDeleting(false); } }, [field]);

  async function handleConfirm() {
    if (!field) return;
    setDeleting(true); setError(null);
    try {
      await apiService.deleteCustomField(field.fieldName, { updated_at: field.updated_at });
      onDeleted(field.fieldName);
      onClose();
    } catch (err: any) {
      setError(err?.message ?? 'Failed to delete custom field');
    } finally {
      setDeleting(false);
    }
  }

  return (
    <Dialog open={!!field} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Delete Custom Field</DialogTitle>
      <DialogContent>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        <DialogContentText>
          Are you sure you want to delete <strong>{field?.fieldName}</strong>? This action cannot be undone.
        </DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={deleting}>Cancel</Button>
        <Button variant="contained" color="error" onClick={handleConfirm} disabled={deleting}>
          {deleting ? <CircularProgress size={18} /> : 'Delete'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ─── Unlock dialog ─────────────────────────────────────────────────────────────

interface UnlockDialogProps {
  field: CustomField | null;
  onClose: () => void;
  onUnlocked: (field: CustomField) => void;
}

function UnlockCustomFieldDialog({ field, onClose, onUnlocked }: UnlockDialogProps) {
  const [unlocking, setUnlocking] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [confirmation, setConfirmation] = useState('');
  const navContainerRef = React.useRef<HTMLElement | null>(null);

  useEffect(() => { if (!field) { setError(null); setUnlocking(false); setConfirmation(''); } }, [field]);

  async function handleConfirm() {
    if (!field) return;
    setUnlocking(true); setError(null);
    try {
      const response = await apiService.unlockCustomField(field.fieldName, { updated_at: field.updated_at });
      onUnlocked(response.data);
      onClose();
    } catch (err: any) {
      setError(err?.message ?? 'Failed to unlock custom field');
    } finally {
      setUnlocking(false);
    }
  }

  const confirmed = confirmation === (field?.fieldName ?? '');

  return (
    <Dialog
      open={!!field}
      onClose={onClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{ ref: navContainerRef }}
    >
      <FormNavigationProvider containerRef={navContainerRef}>
        <DialogTitle>Unlock Custom Field</DialogTitle>
        <DialogContent>
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
          <DialogContentText>
            Unlock <strong>{field?.fieldName}</strong>? Once unlocked, the field can be renamed, changed, or deleted.
          </DialogContentText>
          <DialogContentText sx={{ mt: 1, color: 'error.main', fontWeight: 500, display: 'flex', alignItems: 'center', gap: 0.5, fontSize: '0.75rem' }}>
            <WarningAmberIcon sx={{ fontSize: '0.9rem' }} />
            This action is irreversible. It will permanently delete data that belongs to this field from all employees.
          </DialogContentText>
          <DialogContentText sx={{ mt: 2 }}>
            Type the field name exactly to confirm:
          </DialogContentText>
          <NavTextField
            fullWidth
            size="small"
            sx={{ mt: 1 }}
            placeholder={field?.fieldName}
            value={confirmation}
            onChange={(e) => setConfirmation(e.target.value)}
            onPaste={(e) => e.preventDefault()}
            disabled={unlocking}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} disabled={unlocking}>Cancel</Button>
          <Button variant="contained" color="error" onClick={handleConfirm} disabled={!confirmed || unlocking}>
            {unlocking ? <CircularProgress size={18} /> : 'Unlock'}
          </Button>
        </DialogActions>
      </FormNavigationProvider>
    </Dialog>
  );
}

// ─── History dialog ────────────────────────────────────────────────────────────

interface HistoryDialogProps {
  field: CustomField | null;
  onClose: () => void;
  onUpdated: (field: CustomField) => void;
}

function HistoryDialog({ field, onClose, onUpdated }: HistoryDialogProps) {
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [keepHistory, setKeepHistory] = useState(false);
  const [confirmation, setConfirmation] = useState('');
  const navContainerRef = React.useRef<HTMLElement | null>(null);

  useEffect(() => {
    if (field) {
      setKeepHistory(field.historyEnabled);
      setError(null);
      setSaving(false);
      setConfirmation('');
    }
  }, [field]);

  const changed = keepHistory !== (field?.historyEnabled ?? false);
  const disabling = field?.historyEnabled && !keepHistory;
  const confirmationRequired = disabling && confirmation !== (field?.fieldName ?? '');
  const applyDisabled = !changed || confirmationRequired || saving;

  async function handleApply() {
    if (!field) return;
    setSaving(true); setError(null);
    try {
      const payload: SetHistoryPayload = { historyEnabled: keepHistory, updated_at: field.updated_at };
      const response = await apiService.setCustomFieldHistory(field.fieldName, payload);
      onUpdated(response.data);
      onClose();
    } catch (err: any) {
      setError(err?.message ?? 'Failed to update history setting');
    } finally {
      setSaving(false);
    }
  }

  return (
    <Dialog
      open={!!field}
      onClose={onClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{ ref: navContainerRef }}
    >
      <FormNavigationProvider containerRef={navContainerRef}>
        <DialogTitle>History Tracking</DialogTitle>
        <DialogContent>
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
          <FormControlLabel
            control={<Switch checked={keepHistory} onChange={(e) => setKeepHistory(e.target.checked)} disabled={saving} />}
            label="Keep History"
          />
          {disabling && (
            <>
              <DialogContentText sx={{ mt: 1, color: 'error.main', fontWeight: 500, display: 'flex', alignItems: 'center', gap: 0.5, fontSize: '0.75rem' }}>
                <WarningAmberIcon sx={{ fontSize: '0.9rem' }} />
                This action is irreversible. All historical values will be collapsed to the latest value for every employee.
              </DialogContentText>
              <DialogContentText sx={{ mt: 2 }}>Type the field name exactly to confirm:</DialogContentText>
              <NavTextField
                fullWidth
                size="small"
                sx={{ mt: 1 }}
                placeholder={field?.fieldName}
                value={confirmation}
                onChange={(e) => setConfirmation(e.target.value)}
                onPaste={(e) => e.preventDefault()}
                disabled={saving}
              />
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} disabled={saving}>Cancel</Button>
          <Button
            variant="contained"
            color={disabling ? 'error' : 'primary'}
            onClick={handleApply}
            disabled={applyDisabled}
          >
            {saving ? <CircularProgress size={18} /> : 'Apply'}
          </Button>
        </DialogActions>
      </FormNavigationProvider>
    </Dialog>
  );
}

// ─── View dialog ───────────────────────────────────────────────────────────────

function ViewCustomFieldDialog({ field, onClose }: { field: CustomField | null; onClose: () => void }) {
  if (!field) return null;
  return (
    <Dialog open={!!field} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{field.fieldName}</DialogTitle>
      <DialogContent dividers>
        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
          {([
            ['Field Name', field.fieldName],
            ['Field Type', fieldTypeLabel(field.fieldType)],
            ['Field Title', field.fieldTitle],
            ...(field.fieldType === 'string' ? [['Font Type', fontTypeLabel(field.fontType)]] : []),
            ['Locked', field.locked ? 'Yes' : 'No'],
            ['Version (updated_at)', String(field.updated_at)],
            ...(NUMERIC_TYPES.includes(field.fieldType) ? [
              ['Max Value', field.maxValue != null ? String(field.maxValue) : '—'],
              ['Min Value', field.minValue != null ? String(field.minValue) : '—'],
            ] : []),
          ] as [string, string][]).map(([label, value]) => (
            <Box key={label}>
              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, textTransform: 'uppercase', fontSize: '0.7rem' }}>
                {label}
              </Typography>
              <Typography variant="body2">{value}</Typography>
            </Box>
          ))}
        </Box>
        {field.possibleValues && field.possibleValues.length > 0 && (
          <Box sx={{ mt: 2 }}>
            <Divider sx={{ mb: 1.5 }} />
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, textTransform: 'uppercase', fontSize: '0.7rem' }}>
              Possible Values
            </Typography>
            <List
              dense
              disablePadding
              sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, maxHeight: 180, overflowY: 'auto', mt: 0.5 }}
            >
              {field.possibleValues.map((v, idx) => (
                <ListItem
                  key={v}
                  disablePadding
                  sx={{ px: 1.5, py: 0.25, borderBottom: idx < field.possibleValues!.length - 1 ? '1px solid' : 'none', borderColor: 'divider' }}
                >
                  <ListItemText
                    primary={
                      <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>{v}</Typography>
                    }
                  />
                </ListItem>
              ))}
            </List>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}

// ─── Import Dialog ──────────────────────────────────────────────────────────

interface ImportCandidate {
  fieldName: string;
  fieldTitle: string;
  fieldType: FieldType;
  fontType: FontType;
  possibleValues?: string[];
  historyEnabled: boolean;
  allowDualValues: boolean;
  minValue?: number | null;
  maxValue?: number | null;
  selected: boolean;
}

interface ExistingItem {
  fieldName: string;
  fieldTitle: string;
  reason: string;
}

interface FailedItem {
  fieldName: string;
  fieldTitle: string;
  error: string;
}

interface ImportCustomFieldsDialogProps {
  open: boolean;
  fileName: string;
  candidates: ImportCandidate[];
  existing: ExistingItem[];
  imported: Array<{ fieldName: string; fieldTitle: string }>;
  failed: FailedItem[];
  status: 'preview' | 'importing' | 'completed';
  onClose: () => void;
  onToggleSelect: (index: number) => void;
  onToggleSelectAll: () => void;
  onImport: () => void;
}

const ImportCustomFieldsDialog: React.FC<ImportCustomFieldsDialogProps> = ({
  open,
  fileName,
  candidates,
  existing,
  imported,
  failed,
  status,
  onClose,
  onToggleSelect,
  onToggleSelectAll,
  onImport,
}) => {
  const selectedCount = candidates.filter((c) => c.selected).length;
  const allSelected = candidates.length > 0 && selectedCount === candidates.length;
  const someSelected = selectedCount > 0 && selectedCount < candidates.length;

  return (
    <Dialog
      open={open}
      onClose={() => status !== 'importing' && onClose()}
      maxWidth="md"
      fullWidth
    >
      <DialogTitle sx={{ fontWeight: 600, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <FileUploadIcon />
          <span>Import Custom Fields</span>
        </Box>
        {fileName && (
          <Typography variant="caption" color="text.secondary">
            {fileName}
          </Typography>
        )}
      </DialogTitle>

      <DialogContent dividers>
        {status === 'preview' && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              <Chip label={`Total in File: ${candidates.length + existing.length}`} size="small" />
              <Chip
                label={`Ready to Import: ${candidates.length}`}
                size="small"
                color={candidates.length > 0 ? 'success' : 'default'}
              />
              <Chip
                label={`Selected: ${selectedCount} of ${candidates.length}`}
                size="small"
                color={selectedCount > 0 ? 'primary' : 'default'}
              />
              <Chip
                label={`Skipped / Exists: ${existing.length}`}
                size="small"
                color={existing.length > 0 ? 'warning' : 'default'}
              />
            </Box>

            {existing.length > 0 && (
              <Alert severity="warning" sx={{ py: 0.5 }}>
                <Typography variant="body2" fontWeight={600}>
                  The following {existing.length} field(s) already exist or cannot be imported and will be skipped:
                </Typography>
                <TableContainer component={Paper} variant="outlined" sx={{ mt: 1, maxHeight: 150 }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 600 }}>Field Name</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Field Title</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Reason</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {existing.map((item, idx) => (
                        <TableRow key={idx}>
                          <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.75rem', fontWeight: 500 }}>
                            {item.fieldName}
                          </TableCell>
                          <TableCell sx={{ fontSize: '0.8125rem' }}>{item.fieldTitle}</TableCell>
                          <TableCell>
                            <Chip label={item.reason} size="small" color="warning" variant="outlined" />
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Alert>
            )}

            {candidates.length > 0 ? (
              <Box>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="subtitle2" fontWeight={600}>
                    Fields to Import ({selectedCount} selected of {candidates.length})
                  </Typography>
                  <Button size="small" onClick={onToggleSelectAll}>
                    {allSelected ? 'Deselect All' : 'Select All'}
                  </Button>
                </Box>
                <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 300 }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell padding="checkbox">
                          <Checkbox
                            size="small"
                            checked={allSelected}
                            indeterminate={someSelected}
                            onChange={onToggleSelectAll}
                            inputProps={{ 'aria-label': 'select all fields' }}
                          />
                        </TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Field Name</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Field Title</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Type</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Font</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Details</TableCell>
                        <TableCell sx={{ fontWeight: 600 }} align="center">History</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {candidates.map((item, idx) => (
                        <TableRow
                          key={idx}
                          hover
                          onClick={() => onToggleSelect(idx)}
                          sx={{ cursor: 'pointer', opacity: item.selected ? 1 : 0.6 }}
                        >
                          <TableCell padding="checkbox" onClick={(e) => e.stopPropagation()}>
                            <Checkbox
                              size="small"
                              checked={item.selected}
                              onChange={() => onToggleSelect(idx)}
                            />
                          </TableCell>
                          <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.75rem', fontWeight: 600, color: item.selected ? 'primary.main' : 'text.secondary' }}>
                            {item.fieldName}
                          </TableCell>
                          <TableCell sx={{ fontSize: '0.8125rem' }}>{item.fieldTitle}</TableCell>
                          <TableCell>
                            <Chip label={fieldTypeLabel(item.fieldType)} size="small" variant="outlined" />
                          </TableCell>
                          <TableCell sx={{ fontSize: '0.8125rem', color: 'text.secondary' }}>
                            {item.fieldType === 'string' ? fontTypeLabel(item.fontType) : 'N/A'}
                          </TableCell>
                          <TableCell sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
                            {item.fieldType === 'enum' && item.possibleValues ? (
                              <Chip
                                label={`${item.possibleValues.length} options`}
                                size="small"
                                variant="outlined"
                              />
                            ) : NUMERIC_TYPES.includes(item.fieldType) && ((item.minValue !== null && item.minValue !== undefined) || (item.maxValue !== null && item.maxValue !== undefined)) ? (
                              `[${item.minValue ?? '-∞'}, ${item.maxValue ?? '+∞'}]`
                            ) : item.allowDualValues ? (
                              <Chip label="Dual" size="small" variant="outlined" />
                            ) : (
                              '—'
                            )}
                          </TableCell>
                          <TableCell align="center">
                            {item.historyEnabled ? (
                              <Chip label="Enabled" size="small" color="success" variant="outlined" />
                            ) : (
                              <Typography variant="caption" color="text.secondary">—</Typography>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            ) : (
              <Alert severity="info">
                No new valid custom fields found in this file to import.
              </Alert>
            )}
          </Box>
        )}

        {status === 'importing' && (
          <Box sx={{ py: 4, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
            <CircularProgress size={36} />
            <Typography variant="body1" fontWeight={500}>
              Importing custom fields...
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Please wait while the selected fields are created.
            </Typography>
            <Box sx={{ width: '100%', maxWidth: 400 }}>
              <LinearProgress />
            </Box>
          </Box>
        )}

        {status === 'completed' && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {imported.length > 0 && (
              <Alert severity="success">
                <Typography variant="body2" fontWeight={600}>
                  Successfully imported {imported.length} custom field(s):
                </Typography>
                <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 1 }}>
                  {imported.map((item, idx) => (
                    <Chip
                      key={idx}
                      label={`${item.fieldTitle} (${item.fieldName})`}
                      size="small"
                      color="success"
                      variant="outlined"
                    />
                  ))}
                </Box>
              </Alert>
            )}

            {failed.length > 0 && (
              <Alert severity="error">
                <Typography variant="body2" fontWeight={600}>
                  Failed to import {failed.length} custom field(s):
                </Typography>
                <TableContainer component={Paper} variant="outlined" sx={{ mt: 1, maxHeight: 150 }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 600 }}>Field Name</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Field Title</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Error</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {failed.map((item, idx) => (
                        <TableRow key={idx}>
                          <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>{item.fieldName}</TableCell>
                          <TableCell sx={{ fontSize: '0.8125rem' }}>{item.fieldTitle}</TableCell>
                          <TableCell sx={{ color: 'error.main', fontSize: '0.75rem' }}>{item.error}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Alert>
            )}

            {imported.length === 0 && failed.length === 0 && (
              <Alert severity="info">
                No custom fields were imported.
              </Alert>
            )}
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 1.5 }}>
        {status === 'preview' && (
          <>
            <Button onClick={onClose}>Cancel</Button>
            <Button
              variant="contained"
              onClick={onImport}
              disabled={selectedCount === 0}
              startIcon={<FileUploadIcon />}
            >
              Import Selected ({selectedCount})
            </Button>
          </>
        )}
        {status === 'importing' && (
          <Button disabled>Importing...</Button>
        )}
        {status === 'completed' && (
          <Button variant="contained" onClick={onClose}>
            Done
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

// ─── Main page ─────────────────────────────────────────────────────────────────

const CustomFields: React.FC = () => {
  const dispatch = useAppDispatch();
  const { fields, isLoading, error } = useAppSelector((s) => s.customFields);
  const permissions = useAppSelector((s) => s.auth.permissions);
  const canViewUnlock = permissions.includes('VIEW_CUSTOM_FIELD_UNLOCK');

  const [createOpen, setCreateOpen] = useState(false);
  const [editTarget, setEditTarget] = useState<CustomField | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<CustomField | null>(null);
  const [unlockTarget, setUnlockTarget] = useState<CustomField | null>(null);
  const [historyTarget, setHistoryTarget] = useState<CustomField | null>(null);
  const [viewTarget, setViewTarget] = useState<CustomField | null>(null);

  // Import / Export state
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [importFileName, setImportFileName] = useState('');
  const [candidatesToImport, setCandidatesToImport] = useState<ImportCandidate[]>([]);
  const [alreadyExistingItems, setAlreadyExistingItems] = useState<ExistingItem[]>([]);
  const [importedItems, setImportedItems] = useState<Array<{ fieldName: string; fieldTitle: string }>>([]);
  const [failedItems, setFailedItems] = useState<FailedItem[]>([]);
  const [importStatus, setImportStatus] = useState<'preview' | 'importing' | 'completed'>('preview');

  const handleExport = () => {
    if (fields.length === 0) {
      dispatch(showNotification({ message: 'No custom fields to export', severity: 'warning' }));
      return;
    }

    const exportData = fields.map((f) => ({
      fieldName: f.fieldName,
      fieldTitle: f.fieldTitle,
      fieldType: f.fieldType,
      fontType: f.fontType,
      possibleValues: f.possibleValues && f.possibleValues.length > 0 ? f.possibleValues : undefined,
      historyEnabled: f.historyEnabled,
      allowDualValues: f.allowDualValues,
      minValue: f.minValue ?? undefined,
      maxValue: f.maxValue ?? undefined,
    }));

    const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `custom-fields-${new Date().toISOString().slice(0, 10)}.json`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);

    dispatch(
      showNotification({
        message: `Exported ${exportData.length} custom field(s) successfully`,
        severity: 'success',
      })
    );
  };

  const handleFileSelected = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setImportFileName(file.name);
    const reader = new FileReader();
    reader.onload = (event) => {
      try {
        const text = event.target?.result as string;
        const parsed = JSON.parse(text);
        const list: any[] = Array.isArray(parsed)
          ? parsed
          : Array.isArray(parsed?.customFields)
          ? parsed.customFields
          : Array.isArray(parsed?.data)
          ? parsed.data
          : null;

        if (!list || list.length === 0) {
          dispatch(
            showNotification({
              message: 'Invalid JSON file: Expected a JSON array of custom fields.',
              severity: 'error',
            })
          );
          return;
        }

        const existingByName = new Map(fields.map((f) => [f.fieldName.toLowerCase(), f]));

        const toImport: ImportCandidate[] = [];
        const existing: ExistingItem[] = [];

        for (const raw of list) {
          if (!raw || typeof raw !== 'object') continue;
          let rawName = String(raw.fieldName || raw.name || '').trim();
          let rawTitle = String(raw.fieldTitle || raw.title || raw.label || '').trim();

          if (!rawName && rawTitle) {
            rawName = titleToFieldName(rawTitle);
          }
          if (!rawTitle && rawName) {
            rawTitle = rawName;
          }

          if (!rawName || !rawTitle) {
            existing.push({
              fieldName: rawName || 'N/A',
              fieldTitle: rawTitle || 'N/A',
              reason: 'Missing fieldName or fieldTitle',
            });
            continue;
          }

          if (rawName.endsWith('_B')) {
            existing.push({
              fieldName: rawName,
              fieldTitle: rawTitle,
              reason: "Field name cannot end with '_B' (reserved for system)",
            });
            continue;
          }

          if (!/^[a-zA-Z_$][a-zA-Z0-9_$]*$/.test(rawName)) {
            existing.push({
              fieldName: rawName,
              fieldTitle: rawTitle,
              reason: 'Invalid field name (must start with letter, $, or _ and contain only alphanumeric, $, _)',
            });
            continue;
          }

          const rawType = String(raw.fieldType || raw.type || '').trim().toLowerCase() as FieldType;
          if (!FIELD_TYPES.includes(rawType)) {
            existing.push({
              fieldName: rawName,
              fieldTitle: rawTitle,
              reason: `Invalid field type "${rawType}". Allowed types: ${FIELD_TYPES.join(', ')}`,
            });
            continue;
          }

          if (rawType === 'enum') {
            const possibleVals = Array.isArray(raw.possibleValues)
              ? raw.possibleValues.map((v: any) => String(v).trim()).filter(Boolean)
              : [];
            const uniqueVals = Array.from(new Set(possibleVals.map((v: string) => v.toLowerCase())));
            if (uniqueVals.length < 2) {
              existing.push({
                fieldName: rawName,
                fieldTitle: rawTitle,
                reason: 'Enum fields require at least 2 distinct possible values',
              });
              continue;
            }
          }

          if (existingByName.has(rawName.toLowerCase())) {
            const match = existingByName.get(rawName.toLowerCase())!;
            existing.push({
              fieldName: rawName,
              fieldTitle: rawTitle,
              reason: `Field "${rawName}" already exists (Title: "${match.fieldTitle}")`,
            });
            continue;
          }

          if (toImport.some((c) => c.fieldName.toLowerCase() === rawName.toLowerCase())) {
            existing.push({
              fieldName: rawName,
              fieldTitle: rawTitle,
              reason: 'Duplicate entry in imported file',
            });
            continue;
          }

          const fontType: FontType = raw.fontType === 'bijoy' ? 'bijoy' : 'uni_code';
          const possibleValues = rawType === 'enum'
            ? Array.isArray(raw.possibleValues)
              ? raw.possibleValues.map((v: any) => String(v).trim()).filter(Boolean)
              : []
            : undefined;

          toImport.push({
            fieldName: rawName,
            fieldTitle: rawTitle,
            fieldType: rawType,
            fontType,
            possibleValues,
            historyEnabled: Boolean(raw.historyEnabled),
            allowDualValues: Boolean(raw.allowDualValues),
            minValue: typeof raw.minValue === 'number' ? raw.minValue : null,
            maxValue: typeof raw.maxValue === 'number' ? raw.maxValue : null,
            selected: true,
          });
        }

        setCandidatesToImport(toImport);
        setAlreadyExistingItems(existing);
        setImportedItems([]);
        setFailedItems([]);
        setImportStatus('preview');
        setImportDialogOpen(true);
      } catch (err: any) {
        dispatch(
          showNotification({
            message: 'Failed to parse JSON file: ' + (err?.message || 'Invalid JSON syntax'),
            severity: 'error',
          })
        );
      } finally {
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
      }
    };
    reader.readAsText(file);
  };

  const handleToggleSelect = (index: number) => {
    setCandidatesToImport((prev) =>
      prev.map((item, idx) => (idx === index ? { ...item, selected: !item.selected } : item))
    );
  };

  const handleToggleSelectAll = () => {
    setCandidatesToImport((prev) => {
      const allSelected = prev.every((c) => c.selected);
      return prev.map((item) => ({ ...item, selected: !allSelected }));
    });
  };

  const handleExecuteImport = async () => {
    setImportStatus('importing');
    const selected = candidatesToImport.filter((c) => c.selected);
    const successfullyImported: Array<{ fieldName: string; fieldTitle: string }> = [];
    const updatedAlreadyExisting: ExistingItem[] = [...alreadyExistingItems];
    const failed: FailedItem[] = [];

    for (const item of selected) {
      try {
        const payload: CreateCustomFieldPayload = {
          fieldName: item.fieldName,
          fieldTitle: item.fieldTitle,
          fieldType: item.fieldType,
          fontType: item.fontType,
          possibleValues: item.possibleValues,
          historyEnabled: item.historyEnabled,
          allowDualValues: item.allowDualValues,
          minValue: item.minValue,
          maxValue: item.maxValue,
        };
        const res = await apiService.createCustomField(payload);
        dispatch(addCustomField(res.data));
        successfullyImported.push({ fieldName: res.data.fieldName, fieldTitle: res.data.fieldTitle });
      } catch (err: any) {
        const errorMsg = err?.message || '';
        const isConflict =
          err?.status === 409 ||
          err?.status === 422 ||
          errorMsg.toLowerCase().includes('already in use') ||
          errorMsg.toLowerCase().includes('already exists') ||
          (err?.jsonMessage &&
            typeof err.jsonMessage === 'object' &&
            Object.values(err.jsonMessage).some(
              (v: any) =>
                String(v).toLowerCase().includes('already in use') ||
                String(v).toLowerCase().includes('already exists')
            ));

        if (isConflict) {
          updatedAlreadyExisting.push({
            fieldName: item.fieldName,
            fieldTitle: item.fieldTitle,
            reason: errorMsg || 'Field already exists in database',
          });
        } else {
          failed.push({
            fieldName: item.fieldName,
            fieldTitle: item.fieldTitle,
            error: errorMsg || 'Unknown error occurred',
          });
        }
      }
    }

    setImportedItems(successfullyImported);
    setAlreadyExistingItems(updatedAlreadyExisting);
    setFailedItems(failed);
    setImportStatus('completed');

    if (successfullyImported.length > 0) {
      dispatch(fetchUIFields());
      dispatch(fetchCustomFields());
      dispatch(
        showNotification({
          message: `Successfully imported ${successfullyImported.length} custom field(s)`,
          severity: 'success',
        })
      );
    }
    if (failed.length > 0) {
      dispatch(
        showNotification({
          message: `Failed to import ${failed.length} custom field(s)`,
          severity: 'error',
        })
      );
    }
  };

  function handleCreated(field: CustomField) {
    dispatch(addCustomField(field));
    dispatch(fetchUIFields());
    dispatch(showNotification({ message: `Custom field "${field.fieldName}" created successfully`, severity: 'success' }));
  }

  function handleUpdated(oldName: string, field: CustomField) {
    if (oldName !== field.fieldName) {
      dispatch(removeCustomField(oldName));
      dispatch(addCustomField(field));
    } else {
      dispatch(updateCustomField(field));
    }
    dispatch(fetchUIFields());
    dispatch(showNotification({ message: `Custom field "${field.fieldName}" updated successfully`, severity: 'success' }));
  }

  function handleDeleted(fieldName: string) {
    dispatch(removeCustomField(fieldName));
    dispatch(fetchUIFields());
    dispatch(showNotification({ message: 'Custom field deleted successfully', severity: 'success' }));
  }

  function handleUnlocked(field: CustomField) {
    dispatch(updateCustomField(field));
    dispatch(showNotification({ message: `Custom field "${field.fieldName}" unlocked successfully`, severity: 'success' }));
  }

  function handleHistoryUpdated(field: CustomField) {
    dispatch(updateCustomField(field));
    dispatch(fetchUIFields());
    dispatch(showNotification({ message: `History tracking ${field.historyEnabled ? 'enabled' : 'disabled'} for "${field.fieldName}"`, severity: 'success' }));
  }

  return (
    <DashboardLayout>
      <Breadcrumbs separator={<NavigateNextIcon fontSize="small" />} sx={{ mb: 0.5 }}>
        <Typography variant="body2" color="text.secondary">Employee Entry</Typography>
        <Typography variant="body2" color="text.secondary">Configuration</Typography>
        <Typography variant="body2" color="text.primary" fontWeight={600}>Custom Fields</Typography>
      </Breadcrumbs>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1, gap: 1 }}>
        <Typography variant="h5" sx={{ fontWeight: 600, flexGrow: 1 }}>Custom Fields</Typography>
        <Tooltip title="Refresh">
          <IconButton onClick={() => dispatch(fetchCustomFields())} disabled={isLoading}>
            <RefreshIcon />
          </IconButton>
        </Tooltip>
        <Button
          variant="outlined"
          startIcon={<FileDownloadIcon />}
          onClick={handleExport}
          disabled={isLoading || fields.length === 0}
        >
          Export
        </Button>
        <Button
          variant="outlined"
          startIcon={<FileUploadIcon />}
          onClick={() => fileInputRef.current?.click()}
          disabled={isLoading}
        >
          Import
        </Button>
        <input
          type="file"
          ref={fileInputRef}
          onChange={handleFileSelected}
          accept=".json,application/json"
          style={{ display: 'none' }}
        />
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => setCreateOpen(true)}>
          Create Field
        </Button>
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Manage custom fields used across the system.
      </Typography>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 600 }}>Field Name</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Field Title</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Type</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Font</TableCell>
              <TableCell sx={{ fontWeight: 600 }} align="center">Status</TableCell>
              <TableCell sx={{ fontWeight: 600 }} align="center">History</TableCell>
              <TableCell sx={{ fontWeight: 600 }} align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading && fields.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ py: 4 }}><CircularProgress size={24} /></TableCell>
              </TableRow>
            ) : fields.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                  <Typography variant="body2" color="text.secondary">No custom fields found.</Typography>
                </TableCell>
              </TableRow>
            ) : (
              fields.map((field) => (
                <TableRow key={field.fieldName} hover>
                  <TableCell>
                    <Typography
                      variant="body2"
                      sx={{ fontWeight: 500, fontFamily: 'monospace', cursor: 'pointer', color: 'primary.main' }}
                      onClick={() => setViewTarget(field)}
                    >
                      {field.fieldName}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary">{field.fieldTitle}</Typography>
                  </TableCell>
                  <TableCell>
                    <Chip label={fieldTypeLabel(field.fieldType)} size="small" variant="outlined" />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary">
                      {field.fieldType === 'string' ? fontTypeLabel(field.fontType) : 'N/A'}
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    {field.locked ? (
                      <Chip icon={<LockIcon sx={{ fontSize: 14 }} />} label="Locked" size="small" color="error" variant="filled" />
                    ) : (
                      <Chip label="Unlocked" size="small" color="success" variant="outlined" />
                    )}
                  </TableCell>
                  <TableCell align="center">
                    <Tooltip title={field.historyEnabled ? 'History is enabled' : 'History is disabled'}>
                      <IconButton size="small" onClick={() => setHistoryTarget(field)}>
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="18"
                          height="18"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke={field.historyEnabled ? '#66bb6a' : '#9e9e9e'}
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        >
                          <path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8" />
                          <path d="M3 3v5h5" />
                          <path d="M12 7v5l4 2" />
                        </svg>
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                  <TableCell align="right">
                    <Tooltip title="Edit field">
                      <IconButton size="small" onClick={() => setEditTarget(field)}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    {field.locked && canViewUnlock && (
                      <Tooltip title="Unlock field">
                        <IconButton size="small" color="error" onClick={() => setUnlockTarget(field)}>
                          <LockOpenIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                    <Tooltip title={field.locked ? 'Locked fields cannot be deleted' : 'Delete field'}>
                      <span>
                        <IconButton size="small" color="error" onClick={() => setDeleteTarget(field)} disabled={field.locked}>
                          <DeleteOutlineIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <CreateCustomFieldDialog open={createOpen} onClose={() => setCreateOpen(false)} onCreated={handleCreated} />
      <EditCustomFieldDialog field={editTarget} onClose={() => setEditTarget(null)} onUpdated={handleUpdated} />
      <DeleteCustomFieldDialog field={deleteTarget} onClose={() => setDeleteTarget(null)} onDeleted={handleDeleted} />
      <UnlockCustomFieldDialog field={unlockTarget} onClose={() => setUnlockTarget(null)} onUnlocked={handleUnlocked} />
      <HistoryDialog field={historyTarget} onClose={() => setHistoryTarget(null)} onUpdated={handleHistoryUpdated} />
      <ViewCustomFieldDialog field={viewTarget} onClose={() => setViewTarget(null)} />
      <ImportCustomFieldsDialog
        open={importDialogOpen}
        fileName={importFileName}
        candidates={candidatesToImport}
        existing={alreadyExistingItems}
        imported={importedItems}
        failed={failedItems}
        status={importStatus}
        onClose={() => setImportDialogOpen(false)}
        onToggleSelect={handleToggleSelect}
        onToggleSelectAll={handleToggleSelectAll}
        onImport={handleExecuteImport}
      />

    </DashboardLayout>
  );
};

export default CustomFields;
